import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Image, Trash2, X, Plus, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';
import Navigation from '@/components/Navigation';

function GalleryPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gallery`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data.images || []);
    } catch (error) {
      console.error('Failed to fetch images:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const validFiles = files.filter(file => validTypes.includes(file.type));
    
    if (validFiles.length !== files.length) {
      toast.error('Some files were skipped. Only JPG, PNG, WebP, and GIF are allowed.');
    }

    if (validFiles.length === 0) return;

    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      
      for (const file of validFiles) {
        const formData = new FormData();
        formData.append('file', file);

        await axios.post(`${API}/gallery/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      toast.success(`${validFiles.length} image${validFiles.length > 1 ? 's' : ''} uploaded successfully!`);
      fetchImages();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image(s)');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/gallery/${imageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image deleted');
      setImages(images.filter(img => img.id !== imageId));
      setSelectedImage(null);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete image');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <Image className="w-16 h-16 text-primary animate-pulse mx-auto mb-4" />
            <p className="text-secondary-700 text-lg">Loading gallery...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back to Dashboard
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">
                Your Journey Gallery
              </h1>
              <p className="text-lg text-secondary-600">
                Capture and cherish moments from your Radiant 40 transformation
              </p>
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                id="gallery-upload"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-full shadow-lg"
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload Photos
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-12">
        {images.length === 0 ? (
          <div className="text-center py-20">
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-3xl p-12 max-w-lg mx-auto">
              <Image className="w-20 h-20 text-primary/40 mx-auto mb-6" />
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-3">
                Start Your Visual Journey
              </h2>
              <p className="text-secondary-600 mb-6">
                Upload photos to document your transformation. Capture your progress, 
                celebrate milestones, and create lasting memories.
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-3 rounded-full"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Your First Photo
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {images.map((image) => (
              <div
                key={image.id}
                className="relative group aspect-square rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedImage(image)}
                data-testid={`gallery-image-${image.id}`}
              >
                <img
                  src={image.url}
                  alt={image.caption || 'Gallery image'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white text-sm font-medium truncate">
                      {image.caption || 'Untitled'}
                    </p>
                    <p className="text-white/70 text-xs">
                      {formatDate(image.uploaded_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
              data-testid="close-lightbox"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.caption || 'Gallery image'}
              className="w-full h-full object-contain rounded-lg"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">
                    {selectedImage.caption || 'Untitled'}
                  </p>
                  <p className="text-white/60 text-sm">
                    Uploaded {formatDate(selectedImage.uploaded_at)}
                  </p>
                </div>
                <Button
                  onClick={() => handleDelete(selectedImage.id)}
                  variant="destructive"
                  size="sm"
                  className="bg-red-500/80 hover:bg-red-500"
                  data-testid="delete-image-button"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryPage;
